<template>
  <BaseOverlay :show="fetchingGroup">
    <AddUserToGroupModal
      :filter-by="createdGroup.target"
      @user-added="getGroupUsers"
    />
    <DeleteUserFromGroupModal
      :selected-user="toBeDeletedUser"
      @delete="getGroupUsers"
    />
    <RevokeUsersModal @deleted="getGroupUsers" />

    <!-- Users Reference Type Selection Modal -->
    <b-modal
      id="users-ref-modal"
      v-model="showUsersRefModal"
      title="Select Reference Type"
      hide-footer
      no-close-on-backdrop
    >
      <p class="mb-3">How do you want to reference users in your file?</p>
      <b-form-group>
        <b-form-radio-group
          v-model="usersRef"
          :options="[
            { text: 'Phone Number', value: 'phone_number' },
            { text: 'External ID', value: 'external_id' },
          ]"
        />
      </b-form-group>
      <div class="d-flex justify-content-end">
        <b-button variant="secondary" class="mr-2" @click="cancelUsersRef">
          Cancel
        </b-button>
        <b-button variant="primary" @click="confirmUsersRef">
          Upload File
        </b-button>
      </div>
    </b-modal>

    <div>
      <validation-observer ref="createGroupRef">
        <b-form>
          <b-form-group>
            <validation-provider
              #default="{ errors }"
              name="name"
              rules="required"
            >
              <b-form-input
                id="name"
                v-model.trim="createdGroup.name"
                :disabled="viewMode"
                :state="errors.length > 0 ? false : null"
                name="name"
                placeholder="Enter Group Name"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group>
            <b-form-textarea
              v-model="createdGroup.description"
              placeholder="Enter description"
              :disabled="viewMode"
            />
          </b-form-group>
          <div class="d-flex justify-content-between align-items-center">
            <b-form-group label="Target">
              <b-form-radio-group
                v-model="createdGroup.target"
                :options="targetOptions"
                name="radio-options"
                :disabled="viewMode"
              />
            </b-form-group>
            <BFormCheckbox
              :checked="!createdGroup.locked"
              :disabled="viewMode"
              @change="createdGroup.locked = !$event"
            >
              Could be deleted
            </BFormCheckbox>
          </div>
          <div
            v-if="createdGroup.target == 'riders' && !viewMode"
            class="d-flex align-items-center justify-content-end"
          >
            <b-button
              v-if="!showOffer"
              :disabled="!!groupID && hasOffer"
              variant="outline-primary"
              class="mb-1 mr-1"
              @click="openOfferSection"
            >
              <span>Add Offer</span>
            </b-button>
            <b-button
              v-if="showOffer"
              :disabled="!!groupID && hasOffer"
              variant="outline-primary"
              size="sm"
              class="mb-1"
              @click="closeOfferSection"
            >
              Close Offer Form
              <feather-icon icon="XIcon" />
            </b-button>
          </div>

          <template v-if="createdGroup.target == 'riders' && showOffer">
            <h4>Offer</h4>
            <span>Title</span>
            <b-row>
              <b-col md="6">
                <b-form-group>
                  <validation-provider
                    #default="{ errors }"
                    name="title en"
                    rules="required"
                  >
                    <b-form-input
                      id="title"
                      v-model.trim="createdGroup.offer.title.en"
                      :disabled="viewMode || !!groupID"
                      name="title"
                      placeholder="Enter Title En"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group>
                  <validation-provider
                    #default="{ errors }"
                    name="title ar"
                    rules="required"
                  >
                    <b-form-input
                      id="title"
                      v-model.trim="createdGroup.offer.title.ar"
                      :disabled="viewMode || !!groupID"
                      name="title"
                      placeholder="Enter Title Ar"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
            <span>Message</span>
            <b-row>
              <b-col md="6">
                <b-form-group>
                  <validation-provider
                    #default="{ errors }"
                    name="message en"
                    rules="required"
                  >
                    <b-form-input
                      id="message"
                      v-model.trim="createdGroup.offer.message.en"
                      :disabled="viewMode || !!groupID"
                      name="message"
                      placeholder="Enter Message En"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group>
                  <validation-provider
                    #default="{ errors }"
                    name="message ar"
                    rules="required"
                  >
                    <b-form-input
                      id="message"
                      v-model.trim="createdGroup.offer.message.ar"
                      :disabled="viewMode || !!groupID"
                      name="message"
                      placeholder="Enter Message Ar"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="6">
                <span>Effective Date</span>
                <validation-provider
                  #default="{ errors }"
                  name="effective date"
                  rules="required"
                >
                  <b-form-datepicker
                    v-model="createdGroup.offer.effectiveTime"
                    class="mb-1"
                    :disabled="viewMode || !!groupID"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
                <validation-provider
                  #default="{ errors }"
                  name="effective time"
                  rules="required"
                >
                  <b-form-timepicker
                    v-model="effectiveTimeVal"
                    :disabled="viewMode || !!groupID"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col md="6">
                <span>Expiration Date</span>
                <validation-provider
                  #default="{ errors }"
                  name="expiration date"
                  rules="required"
                >
                  <b-form-datepicker
                    v-model="createdGroup.offer.expirationTime"
                    class="mb-1"
                    :disabled="viewMode || !!groupID"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
                <validation-provider
                  #default="{ errors }"
                  name="expiration time"
                  rules="required"
                >
                  <b-form-timepicker
                    v-model="expirationTimeVal"
                    :disabled="viewMode || !!groupID"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col md="6" class="mt-1">
                <span>Coupon</span>
                <b-form-group>
                  <validation-provider
                    #default="{ errors }"
                    name="coupon"
                    rules="required"
                  >
                    <b-form-input
                      id="coupon"
                      v-model.trim="createdGroup.offer.coupon"
                      :disabled="viewMode || !!groupID"
                      name="coupon"
                      placeholder="Enter Coupon"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </template>

          <div
            v-if="createdGroup.target == 'riders' && !viewMode"
            class="d-flex align-items-center justify-content-end"
          >
            <b-button
              v-if="!showCampaign"
              variant="outline-success"
              class="mb-1"
              :disabled="!!groupID && hasCampaignOffer"
              @click="openCampaignSection"
            >
              <span>Create Campaign</span>
            </b-button>
            <b-button
              v-if="showCampaign"
              :disabled="!!groupID && hasCampaignOffer"
              variant="outline-success"
              size="sm"
              @click="closeCampaignSection"
            >
              Close Campaign Form
              <feather-icon icon="XIcon" />
            </b-button>
          </div>
          <template v-if="createdGroup.target == 'riders' && showCampaign">
            <h4>Campaign</h4>
            <b-row>
              <b-col md="4">
                <b-form-group label="Coupon Code">
                  <validation-provider
                    #default="{ errors }"
                    name="campaign coupon"
                    rules="required"
                  >
                    <b-form-input
                      :disabled="viewMode"
                      v-model.trim="createdGroup.campaignOffer.coupon"
                      placeholder="Enter Coupon Code"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Campaign Logo">
                  <div class="logo-upload-wrapper" @click="openLogoUpload">
                    <input
                      ref="logoFileInput"
                      type="file"
                      accept="image/*"
                      style="display: none"
                      :disabled="viewMode"
                      @change="handleLogoUpload"
                    />
                    <div class="upload-area">
                      <b-spinner v-if="uploadingLogo" variant="primary" small />
                      <span v-else>
                        {{
                          createdGroup.campaignOffer.logo
                            ? "Logo Uploaded"
                            : "Click to upload logo"
                        }}
                      </span>
                    </div>
                  </div>
                  <small class="text-muted">Allowed: JPG, PNG, GIF, SVG</small>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Campaign Status">
                  <BFormCheckbox
                    v-model="createdGroup.campaignOffer.isActive"
                    :disabled="viewMode"
                  >
                    Activate Campaign
                  </BFormCheckbox>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Campaign Time Fields -->
            <b-row class="my-2">
              <b-col md="6">
                <span>Campaign Effective Date</span>
                <validation-provider
                  #default="{ errors }"
                  name="campaign effective date"
                  rules="required"
                >
                  <b-form-datepicker
                    v-model="createdGroup.campaignOffer.effectiveTime"
                    class="mb-1"
                    :disabled="viewMode"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
                <validation-provider
                  #default="{ errors }"
                  name="campaign effective time"
                  rules="required"
                >
                  <b-form-timepicker
                    v-model="campaignEffectiveTimeVal"
                    :disabled="viewMode"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
              <b-col md="6">
                <span>Campaign Expiration Date</span>
                <validation-provider
                  #default="{ errors }"
                  name="campaign expiration date"
                  rules="required"
                >
                  <b-form-datepicker
                    v-model="createdGroup.campaignOffer.expirationTime"
                    class="mb-1"
                    :disabled="viewMode"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
                <validation-provider
                  #default="{ errors }"
                  name="campaign expiration time"
                  rules="required"
                >
                  <b-form-timepicker
                    v-model="campaignExpirationTimeVal"
                    :disabled="viewMode"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>
            </b-row>

            <!-- Driver Messages -->
            <h5>Driver Messages</h5>

            <!-- Dispatch Messages -->
            <span class="font-weight-bold">Dispatch Messages</span>
            <b-row>
              <b-col md="6">
                <b-form-group label="Message (English)">
                  <validation-provider
                    #default="{ errors }"
                    name="dispatch message en"
                    rules="required"
                  >
                    <b-form-textarea
                      v-model.trim="
                        createdGroup.campaignOffer.driverMessages.dispatch
                          .message.en
                      "
                      placeholder="Enter Dispatch Message (EN)"
                      :disabled="viewMode"
                      maxlength="80"
                      @input="handleDispatchEnInput"
                    />
                    <div
                      class="d-flex justify-content-between align-items-center mt-1"
                    >
                      <small class="text-danger">{{ errors[0] }}</small>
                      <small class="text-muted"
                        >{{
                          (
                            createdGroup.campaignOffer.driverMessages.dispatch
                              .message.en || ""
                          ).length
                        }}/80</small
                      >
                    </div>
                    <small v-if="dispatchEnExceeded" class="text-danger"
                      >Dispatch message cannot exceed 80 characters.</small
                    >
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Message (Arabic)">
                  <validation-provider
                    #default="{ errors }"
                    name="dispatch message ar"
                    rules="required"
                  >
                    <b-form-textarea
                      v-model.trim="
                        createdGroup.campaignOffer.driverMessages.dispatch
                          .message.ar
                      "
                      placeholder="Enter Dispatch Message (AR)"
                      :disabled="viewMode"
                      maxlength="80"
                      @input="handleDispatchArInput"
                    />
                    <div
                      class="d-flex justify-content-between align-items-center mt-1"
                    >
                      <small class="text-danger">{{ errors[0] }}</small>
                      <small class="text-muted"
                        >{{
                          (
                            createdGroup.campaignOffer.driverMessages.dispatch
                              .message.ar || ""
                          ).length
                        }}/80</small
                      >
                    </div>
                    <small v-if="dispatchArExceeded" class="text-danger"
                      >Dispatch message cannot exceed 80 characters.</small
                    >
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Arrive Messages -->
            <span class="font-weight-bold">Arrive Messages</span>
            <b-row>
              <b-col md="6">
                <b-form-group label="Message (English)">
                  <validation-provider
                    #default="{ errors }"
                    name="arrive message en"
                    rules="required"
                  >
                    <b-form-textarea
                      v-model.trim="
                        createdGroup.campaignOffer.driverMessages.arrive.message
                          .en
                      "
                      placeholder="Enter Arrive Message (EN)"
                      :disabled="viewMode"
                      maxlength="80"
                      @input="handleArriveEnInput"
                    />
                    <div
                      class="d-flex justify-content-between align-items-center mt-1"
                    >
                      <small class="text-danger">{{ errors[0] }}</small>
                      <small class="text-muted"
                        >{{
                          (
                            createdGroup.campaignOffer.driverMessages.arrive
                              .message.en || ""
                          ).length
                        }}/80</small
                      >
                    </div>
                    <small v-if="arriveEnExceeded" class="text-danger"
                      >Arrive message cannot exceed 80 characters.</small
                    >
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Message (Arabic)">
                  <validation-provider
                    #default="{ errors }"
                    name="arrive message ar"
                    rules="required"
                  >
                    <b-form-textarea
                      v-model.trim="
                        createdGroup.campaignOffer.driverMessages.arrive.message
                          .ar
                      "
                      placeholder="Enter Arrive Message (AR)"
                      :disabled="viewMode"
                      maxlength="80"
                      @input="handleArriveArInput"
                    />
                    <div
                      class="d-flex justify-content-between align-items-center mt-1"
                    >
                      <small class="text-danger">{{ errors[0] }}</small>
                      <small class="text-muted"
                        >{{
                          (
                            createdGroup.campaignOffer.driverMessages.arrive
                              .message.ar || ""
                          ).length
                        }}/80</small
                      >
                    </div>
                    <small v-if="arriveArExceeded" class="text-danger"
                      >Arrive message cannot exceed 80 characters.</small
                    >
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- End Trip Messages - Hidden as requested -->
            <!--
            <span class="font-weight-bold">End Trip Messages</span>
            <b-row>
              <b-col md="6">
                <b-form-group label="Title (English)">
                  <validation-provider
                    #default="{ errors }"
                    name="end trip title en"
                    rules="required"
                  >
                    <b-form-input
                      v-model.trim="createdGroup.campaignOffer.driverMessages.endTrip.title.en"
                      :disabled="viewMode"
                      placeholder="Enter End Trip Title (EN)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Title (Arabic)">
                  <validation-provider
                    #default="{ errors }"
                    name="end trip title ar"
                    rules="required"
                  >
                    <b-form-input
                      v-model.trim="createdGroup.campaignOffer.driverMessages.endTrip.title.ar"
                      :disabled="viewMode"
                      placeholder="Enter End Trip Title (AR)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="6">
                <b-form-group label="Message (English)">
                  <validation-provider
                    #default="{ errors }"
                    name="end trip message en"
                    rules="required"
                  >
                    <b-form-textarea
                      v-model.trim="createdGroup.campaignOffer.driverMessages.endTrip.message.en"
                      :disabled="viewMode"
                      placeholder="Enter End Trip Message (EN)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Message (Arabic)">
                  <validation-provider
                    #default="{ errors }"
                    name="end trip message ar"
                    rules="required"
                  >
                    <b-form-textarea
                      v-model.trim="createdGroup.campaignOffer.driverMessages.endTrip.message.ar"
                      :disabled="viewMode"
                      placeholder="Enter End Trip Message (AR)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
            -->

            <!-- Rider Messages -->
            <h5>Rider Messages</h5>

            <!-- ETA Messages -->
            <span class="font-weight-bold">ETA Messages</span>
            <b-row>
              <b-col md="6">
                <b-form-group label="Message (English)">
                  <validation-provider
                    #default="{ errors }"
                    name="eta message en"
                    rules="required"
                  >
                    <b-form-textarea
                      :disabled="viewMode"
                      v-model.trim="
                        createdGroup.campaignOffer.riderMessages.eta.message.en
                      "
                      placeholder="Enter ETA Message (EN)"
                      maxlength="80"
                      @input="handleEtaEnInput"
                    />
                    <div
                      class="d-flex justify-content-between align-items-center mt-1"
                    >
                      <small class="text-danger">{{ errors[0] }}</small>
                      <small class="text-muted"
                        >{{
                          (
                            createdGroup.campaignOffer.riderMessages.eta.message
                              .en || ""
                          ).length
                        }}/80</small
                      >
                    </div>
                    <small v-if="etaEnExceeded" class="text-danger"
                      >ETA message cannot exceed 80 characters.</small
                    >
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Message (Arabic)">
                  <validation-provider
                    #default="{ errors }"
                    name="eta message ar"
                    rules="required"
                  >
                    <b-form-textarea
                      :disabled="viewMode"
                      v-model.trim="
                        createdGroup.campaignOffer.riderMessages.eta.message.ar
                      "
                      placeholder="Enter ETA Message (AR)"
                      maxlength="80"
                      @input="handleEtaArInput"
                    />
                    <div
                      class="d-flex justify-content-between align-items-center mt-1"
                    >
                      <small class="text-danger">{{ errors[0] }}</small>
                      <small class="text-muted"
                        >{{
                          (
                            createdGroup.campaignOffer.riderMessages.eta.message
                              .ar || ""
                          ).length
                        }}/80</small
                      >
                    </div>
                    <small v-if="etaArExceeded" class="text-danger"
                      >ETA message cannot exceed 80 characters.</small
                    >
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Bill Messages -->
            <span class="font-weight-bold">Bill Messages</span>
            <b-row>
              <b-col md="6">
                <b-form-group label="Message (English)">
                  <validation-provider
                    #default="{ errors }"
                    name="bill message en"
                    rules="required"
                  >
                    <b-form-textarea
                      :disabled="viewMode"
                      v-model.trim="
                        createdGroup.campaignOffer.riderMessages.endTrip.message
                          .en
                      "
                      placeholder="Enter Bill Message (EN)"
                      maxlength="80"
                      @input="handleBillEnInput"
                    />
                    <div
                      class="d-flex justify-content-between align-items-center mt-1"
                    >
                      <small class="text-danger">{{ errors[0] }}</small>
                      <small class="text-muted"
                        >{{
                          (
                            createdGroup.campaignOffer.riderMessages.endTrip
                              .message.en || ""
                          ).length
                        }}/80</small
                      >
                    </div>
                    <small v-if="billEnExceeded" class="text-danger"
                      >Bill message cannot exceed 80 characters.</small
                    >
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Message (Arabic)">
                  <validation-provider
                    #default="{ errors }"
                    name="bill message ar"
                    rules="required"
                  >
                    <b-form-textarea
                      :disabled="viewMode"
                      v-model.trim="
                        createdGroup.campaignOffer.riderMessages.endTrip.message
                          .ar
                      "
                      placeholder="Enter Bill Message (AR)"
                      maxlength="80"
                      @input="handleBillArInput"
                    />
                    <div
                      class="d-flex justify-content-between align-items-center mt-1"
                    >
                      <small class="text-danger">{{ errors[0] }}</small>
                      <small class="text-muted"
                        >{{
                          (
                            createdGroup.campaignOffer.riderMessages.endTrip
                              .message.ar || ""
                          ).length
                        }}/80</small
                      >
                    </div>
                    <small v-if="billArExceeded" class="text-danger"
                      >Bill message cannot exceed 80 characters.</small
                    >
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="12" class="my-1 text-muted">
                <small
                  >Campaigns are separate from the 'Target: Rider and Driver'
                  Radio buttons. They will appear to both riders and drivers
                  after creation</small
                >
              </b-col>
            </b-row>
          </template>

          <b-button
            v-if="!viewMode"
            variant="primary"
            class="d-flex justify-content-end"
            :disabled="loadingCreateBtn"
            @click="createGroup"
          >
            <b-spinner v-if="loadingCreateBtn" small />
            <span v-else>Save</span>
          </b-button>
        </b-form>
      </validation-observer>
    </div>
    <b-card v-if="groupID">
      <base-overlay :show="fetchingUsers">
        <div class="d-flex justify-content-between mb-1">
          <h2>Users</h2>
          <div>
            <b-button
              v-if="!createdGroup.locked"
              variant="danger"
              class="mr-1"
              @click="$bvModal.show('delete-users-group')"
            >
              Revoke all Users
            </b-button>
            <b-button variant="primary" class="mr-1" @click="openAddUserModal">
              Add User
            </b-button>
            <input
              ref="fileInput"
              type="file"
              style="display:none"
              @change="fileHandler"
            />
            <b-button variant="success" :disabled="uploading" @click="openFile">
              <b-spinner v-if="uploading" small />
              <feather-icon v-else icon="UploadIcon" />
            </b-button>
          </div>
        </div>
        <b-table
          fixed
          :fields="tableColumns"
          :items="groupUsers"
          show-empty
          empty-text="No users added to this group."
        >
          <template #cell(actions)="data">
            <b-button
              class="btn-icon rounded-circle"
              variant="flat-danger"
              @click="openDeleteUserModal(data.item.phone_number)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </template>
        </b-table>
        <base-pagination
          v-model="page"
          :total="total"
          @new-per-page="perPage = $event"
        />
      </base-overlay>
    </b-card>
  </BaseOverlay>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import {
  BFormGroup,
  BFormInput,
  BForm,
  BFormTextarea,
  BFormRadioGroup,
  BButton,
  BSpinner,
  BFormCheckbox,
  BCard,
  BTable,
  BRow,
  BCol,
  BFormDatepicker,
  BFormTimepicker,
  BModal,
} from "bootstrap-vue";
import { required } from "@validations";
import BaseOverlay from "@/UI/BaseOverlay.vue";
import useRegisterStoreModule from "@/hooks/useRegisterStoreModule";
import groupsStore from "../store";
import useCreateGroup from "../hooks/usecreateGroup";
import BasePagination from "@/UI/BasePagination.vue";
import AddUserToGroupModal from "./AddUserToGroupModal.vue";
import DeleteUserFromGroupModal from "./DeleteUserFromGroupModal.vue";
import RevokeUsersModal from "./RevokeUsersModal.vue";

export default {
  components: {
    BFormGroup,
    BFormInput,
    BForm,
    BTable,
    BFormTimepicker,
    BFormTextarea,
    RevokeUsersModal,
    DeleteUserFromGroupModal,
    BCard,
    AddUserToGroupModal,
    BasePagination,
    ValidationProvider,
    ValidationObserver,
    BFormRadioGroup,
    BaseOverlay,
    BFormCheckbox,
    BButton,
    BSpinner,
    BRow,
    BCol,
    BFormDatepicker,

  },
  setup(_, ctx) {
    useRegisterStoreModule("groups", groupsStore);
    return {
      required,
      ...useCreateGroup(ctx),
    };
  },
};
</script>

<style scoped>
.checkbox-section {
  gap: 60px;
}

.logo-upload-wrapper {
  cursor: pointer;
}

.upload-area {
  height: 40px;
  border: 1px solid #d8d6de;
  border-radius: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-bottom: 4px;
}

.upload-area:hover {
  background-color: #f8f9fa;
}
</style>
